
import { getStreakRank, getRankDescription } from './streakRanks';
import { generateSequenceId } from './sequenceUtils';

export const generateShareText = async (
  today: string,
  results: string[],
  totalScore: number,
  currentStreak: number,
  gameUrl: string,
  previousStreak?: number,
  levelAttempts?: number[],
  failedLevel?: number,
  isPracticeMode?: boolean
) => {
  // Safety checks to avoid NaN values and ensure all numeric values are properly handled
  const safeScore = isNaN(totalScore) ? 0 : totalScore;
  const safeStreak = isNaN(currentStreak) ? 0 : currentStreak;
  const safePreviousStreak = previousStreak !== undefined && !isNaN(previousStreak) ? previousStreak : undefined;
  
  let shareText = `🅱️ BabbleGram - ${today}${isPracticeMode ? ' (practice mode)' : ''}\n\n`;
  
  shareText += `${results.join('\n')}\n\n`;
  
  shareText += `Score: ${safeScore}\n`;
  
  // Only include streak info in daily mode
  if (!isPracticeMode) {
    // If there was a streak but it's now lost
    if (safeStreak === 0 && safePreviousStreak && safePreviousStreak > 0) {
      const lostRank = getStreakRank(safePreviousStreak);
      shareText += `Streak: 0\n`;
      shareText += `I just lost my streak of ${safePreviousStreak}, I'm no longer a "${lostRank}"\n`;
    } 
    // Active streak - show current streak and rank
    else if (safeStreak > 0) {
      const currentRank = getStreakRank(safeStreak);
      shareText += `Streak: 🔥 ${safeStreak}\n`;
      shareText += `I've achieved the rank of "${currentRank}"\n`;
    } 
    // No streak (never had one or already reset)
    else {
      shareText += "Streak: 0\n";
    }
  }

  // Use provided gameUrl for sharing, defaulting to current origin if not specified
  let shareUrl = gameUrl || window.location.origin;
  
  // In practice mode, add sequence ID to the URL for sharing
  if (isPracticeMode) {
    try {
      // Use the provided today date (which is the sequence date for practice mode)
      const date = new Date(today);
      const sequenceId = generateSequenceId(date);
      const urlParams = new URLSearchParams();
      urlParams.set('mode', 'practice');
      urlParams.set('sequence', sequenceId);
      shareUrl = `${shareUrl}/game?${urlParams.toString()}`;
      
      console.log('🔗 SHARE - Generated share URL:', { 
        providedDate: today,
        parsedDate: date.toISOString(),
        sequenceId,
        shareUrl 
      });
    } catch (error) {
      console.error('Error generating sequence ID for share:', error);
      // Fall back to practice mode URL without sequence
      shareUrl = `${shareUrl}/game?mode=practice`;
    }
  }
  if (isPracticeMode) {
    shareText += `\nSee if you can beat my score! Play this game at: ${shareUrl}`;
  } else {
    shareText += `\nPlay at: ${shareUrl}`;
  }
  
  return shareText;
};

export const shareResults = async (shareText: string): Promise<boolean> => {
  if (!shareText) {
    console.error("Cannot share empty text");
    return false;
  }
  
  const isAndroid = /Android/i.test(navigator.userAgent);
  const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
  const isMobile = isAndroid || isIOS;
  
  console.log("Starting share results operation:", { 
    textLength: shareText.length,
    hasNavigatorShare: !!navigator.share,
    isSecureContext: window.isSecureContext,
    hasClipboard: !!navigator.clipboard,
    isAndroid,
    isIOS,
    isMobile,
    userAgent: navigator.userAgent
  });
  
  // Enhanced Web Share API detection and usage
  if (navigator.share && typeof navigator.share === 'function') {
    try {
      // Check if we can share text
      if (navigator.canShare && !navigator.canShare({ text: shareText })) {
        console.log("Web Share API cannot share text, skipping");
      } else {
        console.log("Attempting Web Share API...");
        await navigator.share({
          title: 'BabbleGram Results',
          text: shareText
        });
        console.log("Successfully shared via Web Share API");
        return true;
      }
    } catch (error: any) {
      console.error("Web Share API failed:", {
        error: error.message,
        name: error.name,
        code: error.code
      });
      
      // If user cancelled, don't try fallbacks
      if (error.name === 'AbortError') {
        console.log("User cancelled share, not trying fallbacks");
        return false;
      }
    }
  } else {
    console.log("Web Share API not available");
  }

  // Skip clipboard API on Android to avoid permission dialogs
  // Only use clipboard API on desktop or if explicitly needed
  if (!isAndroid && window.isSecureContext && navigator.clipboard) {
    try {
      console.log("Attempting clipboard API (non-Android device)...");
      await navigator.clipboard.writeText(shareText);
      console.log("Successfully copied via clipboard API");
      return true;
    } catch (error) {
      console.error("Clipboard API failed:", error);
      // Fall through to fallback method
    }
  } else if (isAndroid) {
    console.log("Skipping clipboard API on Android to avoid permission dialogs");
  }

  // Fallback method using document.execCommand
  try {
    const textArea = document.createElement("textarea");
    textArea.value = shareText;
    
    // Style for minimal visual impact
    textArea.style.position = "fixed";
    textArea.style.left = "0";
    textArea.style.top = "0";
    textArea.style.width = "2em";
    textArea.style.height = "2em";
    textArea.style.padding = "0";
    textArea.style.border = "none";
    textArea.style.outline = "none";
    textArea.style.boxShadow = "none";
    textArea.style.background = "transparent";
    textArea.style.opacity = "0";
    
    document.body.appendChild(textArea);
    textArea.focus();
    textArea.select();
    
    // Special handling for iOS
    if (navigator.userAgent.match(/ipad|iphone/i)) {
      textArea.contentEditable = 'true';
      textArea.readOnly = false;
      
      const range = document.createRange();
      range.selectNodeContents(textArea);
      
      const selection = window.getSelection();
      if (selection) {
        selection.removeAllRanges();
        selection.addRange(range);
        textArea.setSelectionRange(0, 999999);
      }
    }
    
    const successful = document.execCommand('copy');
    document.body.removeChild(textArea);
    
    if (successful) {
      console.log("Copied text using execCommand fallback");
      return true;
    } else {
      console.error("execCommand copy failed");
      return false;
    }
  } catch (err) {
    console.error('Fallback copying failed:', err);
    return false;
  }
};

